import { DefaultNotificationSettings, NotificationFrequency } from "@qendo/notifications";
import { PermissionStatus } from "expo-modules-core";
import { Stack } from "expo-router";
import { useEffect, useState } from "react";
import { Badge } from "~/components/feedback/Badge";
import { FieldGroup } from "~/components/form/FieldGroup";
import { SwitchField } from "~/components/form/SwitchField";
import { NotificationScheduleField } from "~/components/form/schedule/NotificationScheduleField";
import { Page } from "~/components/layout/Page";
import { Only } from "~/components/permission/Only";

import { Space } from "~/constants/sizing";
import { useNotifications } from "~/providers/NotificationsProvider";

export default function Notifications() {
    const { granted, status, settings, canAskAgain, updateSettings, requestPermissions, checkSystemSettings } =
        useNotifications();
    const [enabling, setEnabling] = useState(false);
    const [checking, setChecking] = useState(false);

    const check = () => {
        if (checking || enabling) {
            return;
        }

        setChecking(true);
        checkSystemSettings().finally(() => setChecking(false));
    };

    useEffect(check, []);

    const toggleNotifications = async (enable: boolean) => {
        if (enable && enabling) {
            return;
        } else if (enable) {
            setEnabling(true);

            await requestPermissions()
                .then((granted) => {
                    updateSettings({ push_notifications: granted });
                })
                .finally(() => setEnabling(false));
        } else {
            updateSettings({ push_notifications: false });
        }
    };

    const notifications_enabled = granted && settings.push_notifications;
    const cannot_enable = status === PermissionStatus.DENIED && !canAskAgain;

    return (
        <>
            <Stack.Screen options={{ title: "Notifications" }} />
            <Page spacer isRefreshing={checking} refreshWith={checkSystemSettings}>
                <FieldGroup>
                    {cannot_enable && (
                        <Badge
                            title="Notifications have been disabled in your phone settings. Turn them back on to enable notifications."
                            variant="warning"
                        />
                    )}
                    <SwitchField
                        label="Push notifications"
                        value={settings.push_notifications || enabling}
                        disabled={cannot_enable}
                        onValueChange={toggleNotifications}
                    />
                </FieldGroup>
                <Only when={notifications_enabled}>
                    <FieldGroup title="Calendar">
                        <SwitchField
                            label="Upcoming appointments"
                            value={settings.upcoming_appointments}
                            onValueChange={(value) => updateSettings({ upcoming_appointments: value })}
                        />
                    </FieldGroup>
                    <FieldGroup title="Cycle" gap={Space.md}>
                        <SwitchField
                            label="Period prediction"
                            value={settings.period_prediction}
                            onValueChange={(value) => updateSettings({ period_prediction: value })}
                        />
                        <SwitchField
                            label="Ovulation prediction"
                            value={settings.ovulation_prediction}
                            onValueChange={(value) => updateSettings({ ovulation_prediction: value })}
                        />
                    </FieldGroup>
                    <FieldGroup title="Self care" gap={Space.md}>
                        <SwitchField
                            label="Pain check-ins"
                            value={settings.pain}
                            hint="After multiple high-pain entries in a week"
                            onValueChange={(value) => updateSettings({ pain: value })}
                        />
                        <NotificationScheduleField<NotificationFrequency.daily | NotificationFrequency.specific_days>
                            label="Personal affirmations"
                            value={settings.affirmations}
                            fresh={DefaultNotificationSettings.affirmations}
                            allowedFrequencies={[NotificationFrequency.daily, NotificationFrequency.specific_days]}
                            onUpdateSchedule={(value) => updateSettings({ affirmations: value })}
                        />
                        <NotificationScheduleField<
                            | NotificationFrequency.daily
                            | NotificationFrequency.specific_days
                            | NotificationFrequency.interval
                        >
                            label="Take a Moment to Breathe"
                            value={settings.breath}
                            fresh={DefaultNotificationSettings.breath}
                            allowedFrequencies={[
                                NotificationFrequency.daily,
                                NotificationFrequency.specific_days,
                                NotificationFrequency.interval,
                            ]}
                            onUpdateSchedule={(value) => updateSettings({ breath: value })}
                        />
                    </FieldGroup>
                    <FieldGroup title="Wellbeing" gap={Space.md}>
                        <SwitchField
                            label="Take medication"
                            value={settings.take_medication}
                            onValueChange={(value) => updateSettings({ take_medication: value })}
                        />
                        <NotificationScheduleField
                            label="Log pain"
                            value={settings.log_pain}
                            fresh={DefaultNotificationSettings.log_pain}
                            onUpdateSchedule={(value) => updateSettings({ log_pain: value })}
                        />
                        <NotificationScheduleField
                            label="Log symptom"
                            value={settings.log_symptom}
                            fresh={DefaultNotificationSettings.log_symptom}
                            onUpdateSchedule={(value) => updateSettings({ log_symptom: value })}
                        />
                        <NotificationScheduleField
                            label="Drink water"
                            value={settings.drink_water}
                            fresh={DefaultNotificationSettings.drink_water}
                            onUpdateSchedule={(value) => updateSettings({ drink_water: value })}
                        />
                        <NotificationScheduleField
                            label="Pelvic exercises"
                            value={settings.pelvic_exercises}
                            fresh={DefaultNotificationSettings.pelvic_exercises}
                            onUpdateSchedule={(value) => updateSettings({ pelvic_exercises: value })}
                        />
                        <NotificationScheduleField
                            label="Move your body"
                            value={settings.move_your_body}
                            fresh={DefaultNotificationSettings.move_your_body}
                            onUpdateSchedule={(value) => updateSettings({ move_your_body: value })}
                        />
                        <NotificationScheduleField
                            label="Rest"
                            value={settings.rest}
                            fresh={DefaultNotificationSettings.rest}
                            onUpdateSchedule={(value) => updateSettings({ rest: value })}
                        />
                    </FieldGroup>
                </Only>
            </Page>
        </>
    );
}
